import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ClearIcon from "@mui/icons-material/Clear";
import AssignmentIcon from "@mui/icons-material/Assignment";
import DescriptionIcon from "@mui/icons-material/Description";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import BusinessIcon from "@mui/icons-material/Business";
import ScheduleIcon from "@mui/icons-material/Schedule";
import StraightenIcon from "@mui/icons-material/Straighten";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import NoteIcon from "@mui/icons-material/Note";
import BuildIcon from "@mui/icons-material/Build";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import ScienceIcon from "@mui/icons-material/Science";
import NotesIcon from "@mui/icons-material/Notes";
import { useNavigate } from "react-router-dom";
import { Project } from "../../types/project";
import MapPreview from "../../components/MapPreview";
import { nl2br } from "../../utils/helper";
import { formatWithTimezone } from "../../utils/dateHelper";
import { decode } from "html-entities";
import { GlassDrawer } from "../../components/global";
import { useUILabel } from "../../hooks/useUILabel";

const containerStyle = {
  width: "100%",
  height: "400px",
};

const options = {
  fullscreenControl: false, // remove the top-right button
  mapTypeControl: false, // remove the top-left buttons
  streetViewControl: false, // remove the pegman
  zoomControl: true, // remove the bottom-right buttons
  scrollwheel: false,
};

interface ProjectDrawerProps {
  data: Project;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function ProjectDrawer(props: ProjectDrawerProps) {
  const { data, open, setOpen } = props;

  const navigate = useNavigate();
  const locationAddressLabel = useUILabel("common.locationAddress");
  const clientAddressLabel = useUILabel("common.clientAddress");
  const workIdWorkTitleLabel = useUILabel("project.workIdWorkTitle");
  const scopeOfWorkLabel = useUILabel("project.scopeOfWork");
  const startAtLabel = useUILabel("project.startAt");
  const estimatedAmountOfHoursLabel = useUILabel("project.estimatedAmountOfHours");
  const mileRadiusLabel = useUILabel("project.mileRadius");
  const photosRequiredLabel = useUILabel("project.photosRequired");
  const finalNotesRequiredLabel = useUILabel("project.finalNotesRequired");
  const toolsRequiredLabel = useUILabel("project.toolsRequired");
  const backgroundCheckRequiredLabel = useUILabel("project.backgroundCheckRequiredLabel");
  const drugTestRequiredLabel = useUILabel("project.drugTestRequiredLabel");
  const internalNotesLabel = useUILabel("project.internalNotes");
  const moreDetailLabel = useUILabel("project.moreDetail");
  const yesLabel = useUILabel("project.yes");
  const noLabel = useUILabel("project.no");
  const nALabel = useUILabel("project.nA");

  const [lng, lat] = React.useMemo(() => {
    if (!!data?.customer) {
      return data?.customer?.coordinate?.coordinates || [];
    } else if (!!data?.location) {
      return data?.location?.coordinate?.coordinates || [];
    } else return [];
  }, [data]);

  const toggleDrawer =
    (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === "keydown" &&
        ((event as React.KeyboardEvent).key === "Tab" ||
          (event as React.KeyboardEvent).key === "Shift")
      ) {
        return;
      }

      setOpen(open);
    };

  const list = () => (
    <>
      <Box
        sx={{
          display: { xs: "flex", sm: "none" },
          justifyContent: "flex-end",
          mt: { xs: 0.5, sm: 1.5 },
        }}
      >
        <ClearIcon
          onClick={toggleDrawer(false)}
          onKeyDown={toggleDrawer(false)}
          sx={{ cursor: "pointer" }}
        />
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {/* {lat && lng && ( */}
        <Box
          sx={{
            px: { xs: 0.5, sm: 1 },
            borderRadius: 2,
            overflow: "hidden",
            width: "100%",
            height: { xs: 200, sm: 240 },
            minHeight: { xs: 200, sm: 240 },
            flexShrink: 0,
            "& > div": {
              borderRadius: 2,
              width: "100%",
              height: "100%",
            },
          }}
        >
          <MapPreview lat={lat || 0} lng={lng || 0} />
        </Box>
        {/* )} */}
        <List
          sx={{
            "& .MuiListItem-root": {
              px: { xs: 0.5, sm: 1 },
            },
          }}
        >
          <ListItem disableGutters>
            <ListItemText
              primaryTypographyProps={{ fontSize: { xs: 12, sm: 14 }, color: "#12903C", fontWeight: 600 }}
              primary={workIdWorkTitleLabel}
              secondary={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                  <AssignmentIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: "text.secondary", flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14 } }}>
                    {`#${data?.id} ${decode(data?.title)}`}
                  </Typography>
                </Box>
              }
            />
          </ListItem>
          <Divider />
          <ListItem disableGutters>
            <ListItemText
              primaryTypographyProps={{ fontSize: { xs: 12, sm: 14 }, color: "#12903C", fontWeight: 600 }}
              primary={scopeOfWorkLabel}
              secondary={
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mt: 0.5 }}>
                  <DescriptionIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: "text.secondary", flexShrink: 0, mt: 0.25 }} />
                  <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14 } }} component="div">
                    {nl2br(decode(data?.description))}
                  </Typography>
                </Box>
              }
            />
          </ListItem>
          <Divider />
          <ListItem disableGutters>
            <ListItemText
              primaryTypographyProps={{ fontSize: { xs: 12, sm: 14 }, color: "#12903C", fontWeight: 600 }}
              primary={startAtLabel}
              secondary={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                  <CalendarTodayIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: "text.secondary", flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14 } }}>
                    {formatWithTimezone(data?.start_date, data?.timezone)}
                  </Typography>
                </Box>
              }
            />
          </ListItem>
          <Divider />
          {!!data?.location && (
            <ListItem disableGutters>
              <ListItemText
                primaryTypographyProps={{ fontSize: { xs: 12, sm: 14 }, color: "#12903C", fontWeight: 600 }}
                primary={locationAddressLabel}
                secondary={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                    <LocationOnIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: "text.secondary", flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14 } }}>
                      {`${data?.location.address_1}, ${data?.location.city}, ${data?.location.zip}`}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          )}
          {!!data?.customer && (
            <ListItem disableGutters>
              <ListItemText
                primaryTypographyProps={{ fontSize: { xs: 12, sm: 14 }, color: "#12903C", fontWeight: 600 }}
                primary={clientAddressLabel}
                secondary={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                    <BusinessIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: "text.secondary", flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14 } }}>
                      {`${data?.customer?.physical_address_1}, ${data?.customer?.city}, ${data?.customer?.zip}`}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          )}
          <Divider />
          <ListItem disableGutters>
            <ListItemText
              primaryTypographyProps={{ fontSize: { xs: 12, sm: 14 }, color: "#12903C", fontWeight: 600 }}
              primary={estimatedAmountOfHoursLabel}
              secondary={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                  <ScheduleIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: "text.secondary", flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14 } }}>
                    {`${data?.approximate_hours_to_complete} hr`}
                  </Typography>
                </Box>
              }
            />
          </ListItem>
          <Divider />
          <ListItem disableGutters>
            <ListItemText
              primaryTypographyProps={{ fontSize: { xs: 12, sm: 14 }, color: "#12903C", fontWeight: 600 }}
              primary={mileRadiusLabel}
              secondary={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                  <StraightenIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: "text.secondary", flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14 } }}>
                    {`${data?.mile_radius} miles`}
                  </Typography>
                </Box>
              }
            />
          </ListItem>
          <Divider />
          <ListItem disableGutters>
            <ListItemText
              primaryTypographyProps={{ fontSize: { xs: 12, sm: 14 }, color: "#12903C", fontWeight: 600 }}
              primary={photosRequiredLabel}
              secondary={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                  <CameraAltIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: "text.secondary", flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14 } }}>
                    {!!data?.is_photo_required ? yesLabel : noLabel}
                  </Typography>
                </Box>
              }
            />
          </ListItem>
          <Divider />
          <ListItem disableGutters>
            <ListItemText
              primaryTypographyProps={{ fontSize: { xs: 12, sm: 14 }, color: "#12903C", fontWeight: 600 }}
              primary={finalNotesRequiredLabel}
              secondary={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                  <NoteIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: "text.secondary", flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14 } }}>
                    {!!data?.is_final_notes_required ? yesLabel : noLabel}
                  </Typography>
                </Box>
              }
            />
          </ListItem>
          <Divider />
          <ListItem disableGutters>
            <ListItemText
              primaryTypographyProps={{ fontSize: { xs: 12, sm: 14 }, color: "#12903C", fontWeight: 600 }}
              primary={toolsRequiredLabel}
              secondary={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                  <BuildIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: "text.secondary", flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14 } }}>
                    {!!data?.tool_required ? yesLabel : noLabel}
                  </Typography>
                </Box>
              }
            />
          </ListItem>
          <Divider />
          <ListItem disableGutters>
            <ListItemText
              primaryTypographyProps={{ fontSize: { xs: 12, sm: 14 }, color: "#12903C", fontWeight: 600 }}
              primary={backgroundCheckRequiredLabel}
              secondary={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                  <VerifiedUserIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: "text.secondary", flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14 } }}>
                    {!!data?.background_check_required ? yesLabel : noLabel}
                  </Typography>
                </Box>
              }
            />
          </ListItem>
          <Divider />
          <ListItem disableGutters>
            <ListItemText
              primaryTypographyProps={{ fontSize: { xs: 12, sm: 14 }, color: "#12903C", fontWeight: 600 }}
              primary={drugTestRequiredLabel}
              secondary={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                  <ScienceIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: "text.secondary", flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14 } }}>
                    {!!data?.drug_test_required ? yesLabel : noLabel}
                  </Typography>
                </Box>
              }
            />
          </ListItem>
          <Divider />
          <ListItem disableGutters>
            <ListItemText
              primaryTypographyProps={{ fontSize: { xs: 12, sm: 14 }, color: "#12903C", fontWeight: 600 }}
              primary={internalNotesLabel}
              secondary={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                  <NotesIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: "text.secondary", flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14 } }}>
                    {data?.internal_notes || nALabel}
                  </Typography>
                </Box>
              }
            />
          </ListItem>
        </List>
      </Box>
      <Box display="flex" justifyContent="center" sx={{ mt: 2 }}>
        <Button
          variant="contained"
          fullWidth
          onClick={() => navigate(`/works/${data?.id}`)}
        >
          {moreDetailLabel}
        </Button>
      </Box>
    </>
  );

  return (
    <GlassDrawer
      anchor="right"
      open={open}
      onClose={toggleDrawer(false)}
      ModalProps={{ keepMounted: true }}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 420, md: 460 },
        },
      }}
      cardProps={{
        sx: {
          p: { xs: 1.5, sm: 2 },
        },
      }}
      contentProps={{
        role: "presentation",
        sx: {
          display: "flex",
          flexDirection: "column",
          gap: 2,
          flex: 1,
          minHeight: 0,
          px: 0,
          py: 0,
        },
      }}
    >
      {list()}
    </GlassDrawer>
  );
}
